import { useSyncExternalStore } from 'react';

/**
 * Sesión del cliente.
 * - `accessToken` (15 min) vive **solo en memoria**: no queda en disco ni es legible tras cerrar la app.
 * - `refreshToken` (30 d, rotado en cada uso) se guarda en `localStorage` para que la PWA recuerde la
 *   sesión; se borra al cerrar sesión o si la API lo rechaza (`PZ-AUTH-004`).
 * Cuando la API exponga cookie `HttpOnly` para el refresh, solo cambia este módulo.
 */
const STORAGE_KEY = 'paguz.web.refresh.v1';

export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated';

export interface SessionState {
  readonly status: SessionStatus;
  readonly accessToken: string | null;
}

let state: SessionState = { status: 'unknown', accessToken: null };
const listeners = new Set<() => void>();

function emit(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

function readStorage(): string | null {
  try {
    return globalThis.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(value: string | null): void {
  try {
    if (value === null) globalThis.localStorage.removeItem(STORAGE_KEY);
    else globalThis.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Navegación privada o almacenamiento bloqueado: la sesión dura lo que dure la pestaña.
  }
}

let memoryRefreshToken: string | null = null;

export const session = {
  getState: (): SessionState => state,
  getAccessToken: (): string | null => state.accessToken,
  getRefreshToken: (): string | null => memoryRefreshToken ?? readStorage(),

  /** Guarda un par de tokens nuevo (login, registro o refresh rotado). */
  setTokens(tokens: { accessToken: string; refreshToken: string }): void {
    memoryRefreshToken = tokens.refreshToken;
    writeStorage(tokens.refreshToken);
    emit({ status: 'authenticated', accessToken: tokens.accessToken });
  },

  /** Sin sesión (logout, refresh rechazado o no había sesión guardada). */
  clear(): void {
    memoryRefreshToken = null;
    writeStorage(null);
    emit({ status: 'anonymous', accessToken: null });
  },

  /** Sin sesión activa pero conservando el refresh token (p. ej. sin red al abrir la app). */
  markAnonymous(): void {
    emit({ status: 'anonymous', accessToken: null });
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Solo para pruebas. */
  reset(): void {
    memoryRefreshToken = null;
    writeStorage(null);
    state = { status: 'unknown', accessToken: null };
  },
};

export function useSessionState(): SessionState {
  return useSyncExternalStore(
    (listener) => session.subscribe(listener),
    () => session.getState(),
    () => session.getState(),
  );
}
